import React, { useCallback, useLayoutEffect } from 'react';
import { useNavigation } from '@react-navigation/native';
import { LoadingAnimation, useResponsive, useToggleState } from '@sg/garnish';
import { IngredientAssetVersion, type Vendor } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  useCustomizationContext,
  useInitCustomizationMachine,
} from '@sg/shared-fe/features/customization';

import { NotFoundView } from '@order/components';
import { useSetLastInteractedMenuStore } from '@order/LastInteractedStore';

import {
  useCustomizationExternalActions,
  useCustomizeCartMutations,
} from './hooks';
import { CustomizationCustomizeView, CustomizationDetailsView } from './views';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationV2 = (props: Props) => {
  const { restaurantSlug, productSlug } = props;

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!restaurantSlug || !productSlug) {
    return <NotFoundView />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return <CustomizationReady {...props} />;
};

const CustomizationReady = (props: Props) => {
  const {
    restaurantSlug,
    productSlug,
    addressId,
    deliveryFee,
    vendor,
    reorderLineItemId,
    lineItemId,
    isEditProductMode,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
  } = props;

  const { setOptions } = useNavigation();
  const { currentBreakpoint } = useResponsive();

  // ─── State ───────────────────────────────────────────────────────────

  const {
    value: isOutOfStockModalOpen,
    toggleOn: openOutOfStockModal,
    toggleOff: closeOutOfStockModal,
  } = useToggleState();

  // ─────────────────────────────────────────────────────────────────────

  const {
    onMaxModificationsExceeded,
    onMaxKindQtyExceeded,
    onMaxKindExceeded,
    onMinUnmet,
    onFailedToAddModification,
    onOutOfStockIngredientsFound,
    onModificationAdded,
    onModificationRemoved,
    onCustomizationEnd,
    onCustomizationCancel,
  } = useCustomizationExternalActions();

  const handleOutOfStockIngredientsFound =
    useCallback<CustomizationMachineTypes.CustomizationMachineAction>(
      (...params) => {
        onOutOfStockIngredientsFound?.(...params);
        openOutOfStockModal();
      },
      [openOutOfStockModal, onOutOfStockIngredientsFound],
    );

  const actor = useInitCustomizationMachine(
    {
      productSlug,
      restaurantSlug,
      lineItemId: lineItemId ?? reorderLineItemId,
      ingredientAssetVersion: IngredientAssetVersion.V2,
      isDelivery: Boolean(addressId),
    },
    {
      actions: {
        onOutOfStockIngredientsFound: handleOutOfStockIngredientsFound,
        onMaxModificationsExceeded,
        onMaxKindQtyExceeded,
        onMaxKindExceeded,
        onMinUnmet,
        onFailedToAddModification,
        onModificationAdded,
        onModificationRemoved,
        onCustomizationEnd,
        onCustomizationCancel,
      },
    },
  );

  // ─── Context ─────────────────────────────────────────────────────────

  const {
    isFetching,
    hasFailedToFetch,
    isCustomizationActive,
    product,
    restaurant,
  } = useCustomizationContext({ actor });

  // ─── Line Item Interactions ──────────────────────────────────────────

  const { modifyCart, isAddingLineItemToCart, isEditingLineItemInCart } =
    useCustomizeCartMutations({
      actor,
      addressId,
      deliveryFee,
      vendor,
      lineItemId,
      onAddLineItemToCart,
      onEditLineItemInCart,
      onRemoveLineItemFromCart,
    });

  const shouldDisableControls =
    isAddingLineItemToCart || isEditingLineItemInCart;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions({ headerShown: !currentBreakpoint.isXS });
  }, [currentBreakpoint.isXS, setOptions]);

  useSetLastInteractedMenuStore({
    restaurantId: restaurant?.id,
    restaurantName: restaurant?.name,
    restaurantSlug: restaurant?.slug,
    addressId,
    deliveryFee,
    vendor: vendor?.name,
    vendorRestaurantId: vendor?.restaurantId,
  });

  // ─────────────────────────────────────────────────────────────────────

  if (isFetching) {
    return <LoadingAnimation />;
  }

  if (hasFailedToFetch || !product || !restaurant) {
    return <NotFoundView />;
  }

  // ─── Customization ───────────────────────────────────────────────────

  if (isCustomizationActive) {
    return (
      <CustomizationCustomizeView
        actor={actor}
        addressId={addressId}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        closeOutOfStockModal={closeOutOfStockModal}
      />
    );
  }

  // ─── Dressing Details ────────────────────────────────────────────────

  return (
    <CustomizationDetailsView
      actor={actor}
      addressId={addressId}
      isOutpost={restaurant.isOutpost}
      isEditProductMode={isEditProductMode}
      shouldDisableControls={shouldDisableControls}
      isOutOfStockModalOpen={isOutOfStockModalOpen}
      closeOutOfStockModal={closeOutOfStockModal}
      modifyCart={modifyCart}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restaurantSlug: string;
  productSlug: string;
  reorderLineItemId?: string;
  lineItemId?: string;
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  isEditProductMode: boolean;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
};
